import React from 'react';
import './tags.css';

/**
 * The app's small inline vocabulary. Only things you can press (FilterChip)
 * and yes/no properties that must stand out (Flag) are boxed; everything else
 * is text, so a panel of numbers and labels stays calm and the few boxes mean
 * something.
 *
 * - TagRow      wraps a run of the pieces below with even spacing
 * - Stat        a label and its value ("Power 7.2")
 * - Category    a category or type name, with a dot only when its colour is data
 * - StatusMark  a positive / watch / negative note, the tone carried by its mark
 * - Flag        a yes/no property ("Wincon", "Private"), hairline-boxed
 * - Grade       a graded level as a four-step meter ("Density ●●●○")
 *
 * Tones: 'positive' | 'watch' | 'negative' | 'neutral' (the default).
 */

export function TagRow({ children, className = '', ...rest }) {
    return (
        <span className={`tagRow${className ? ` ${className}` : ''}`} {...rest}>
            {children}
        </span>
    );
}

export function Stat({ label, value, tone = 'neutral', title }) {
    return (
        <span className={`tagStat tagStat--${tone}`} title={title}>
            {label != null && <span className="tagStatLabel">{label}</span>}
            <span className="tagStatValue">{value}</span>
        </span>
    );
}

export function Category({ label, color, title }) {
    return (
        <span className="tagCategory" title={title}>
            {color && <span className="tagCategoryDot" style={{ background: color }} aria-hidden="true" />}
            {label}
        </span>
    );
}

export function StatusMark({ tone = 'neutral', children, title }) {
    return (
        <span className={`tagStatus tagStatus--${tone}`} title={title}>
            <span className="tagStatusMark" aria-hidden="true" />
            {children}
        </span>
    );
}

export function Flag({ tone = 'neutral', children, title }) {
    return (
        <span className={`tagFlag tagFlag--${tone}`} title={title}>
            {children}
        </span>
    );
}

/** `level` 0–4; `levelLabel` is the word it stands for, read out to screen readers. */
export function Grade({ label, level, levelLabel, max = 4 }) {
    const filled = Math.max(0, Math.min(max, Math.round(level || 0)));
    return (
        <span className="tagGrade" title={levelLabel}>
            {label && <span className="tagGradeLabel">{label}</span>}
            <span className="tagGradeMeter" role="img" aria-label={levelLabel || `${filled} of ${max}`}>
                {Array.from({ length: max }, (_, i) => (
                    <span key={i} className={`tagGradeStep${i < filled ? ' is-on' : ''}`} />
                ))}
            </span>
        </span>
    );
}

/**
 * A filter toggle: the system filter chip, hairline at rest and solid gold when on.
 * Given `onRemove`, it is instead an applied value with its own remove button
 * (named by `removeLabel`), so no button ever nests inside another.
 */
export function FilterChip({ label, count, icon, pressed, onPress, onRemove, removeLabel, title, className = '' }) {
    if (onRemove) {
        return (
            <span
                className={`filterChip is-active filterChip--removable${className ? ` ${className}` : ''}`}
                title={title}
            >
                {icon}
                <span className="filterChipLabel">{label}</span>
                <button type="button" className="filterChipRemove" onClick={onRemove} aria-label={removeLabel}>
                    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                        <path d="M4.5 4.5 L11.5 11.5 M11.5 4.5 L4.5 11.5" />
                    </svg>
                </button>
            </span>
        );
    }
    return (
        <button
            type="button"
            className={`filterChip${pressed ? ' is-active' : ''}${className ? ` ${className}` : ''}`}
            aria-pressed={Boolean(pressed)}
            onClick={onPress}
            title={title}
        >
            {icon}
            <span className="filterChipLabel">{label}</span>
            {count != null && <span className="filterChipCount">{count}</span>}
        </button>
    );
}
